import React, { useContext } from 'react';
import { Query } from 'react-apollo';
import classnames from 'classnames';

import Icon, { Icons } from '@shared/Icons/Icon';
import TileSet from '../../../../shared/TileSet';
import LoadingTileSet from '../../../../shared/TileSet/LoadingTileSet';
import EventTile from '../../../../shared/Tiles/EventTile';
import eventResolver from '../../../../state/resolvers/event';
import AppContext from '../../../../context';
import { ContextName } from '../../../../appContext/contextName';
import { getSearchParams } from '../../utils/searchParams';
import { SearchEventsV2 } from '../../../../state/services/gql/content';
import { getBrowserLocation } from '../../../../appContext/LocationProvider';
import { AccuracyEnum, StatusEnum } from '../../../../appContext/location';
import { SEGMENT_EVENTS } from '../../../../utils/tracking';
import { withTracking, withLocationInfo } from '../../provider';
import { getEmptyViewpointState } from '../../provider/location';
import {
  header,
  title,
  container,
  torchRedLink,
  notCorrectSpan,
  correctLocationContainer,
  showDesktop,
  showTablet,
} from './styles.module.css';

const getTitle = ({ loading, sport, locationName }) => {
  if (loading) {
    return `Nearby ${sport}`;
  }

  return `${sport} near ${locationName}`;
};

const getSeeAllTitle = ({ sport, locationName }) => {
  return `See all ${sport.toLowerCase()} near ${locationName}`;
};

// TODO:(tomo@) Make this component independant from
//              category pages. Abstract injected params
const NearbyEvents = ({
  data,
  loading,
  locationName,
  injected: {
    tracking: { trackButtonClick, trackGenericEvent },
    location: { setPoint },
  },
  sport,
}) => {
  const {
    [ContextName.LOCATION.LATITUDE]: latitude,
    [ContextName.LOCATION.LONGITUDE]: longitude,
    [ContextName.LOCATION.ACCURACY]: locationAccuracy,
    [ContextName.LOCATION.UPDATE_COORDS]: updateCoords,
    [ContextName.LOCATION.UPDATE_NAME]: updateLocationName,
  } = useContext(AppContext);

  const events = data?.searchEventsV2?.events
    ?.slice?.(0, 6)
    .map?.(eventResolver);

  const showNotCorrectLocationButton =
    locationAccuracy === AccuracyEnum.IP_STACK;

  const getLocationOnClick = async () => {
    trackButtonClick('get_current_location');
    try {
      const {
        locationName: updatedLocationName,
        ...location
      } = await getBrowserLocation();

      updateCoords({ latitude: null, longitude: null });
      updateCoords(location);
      updateLocationName({ locationName: updatedLocationName });

      trackGenericEvent(
        SEGMENT_EVENTS.GEOLOCATION_ALLOWED,
        'get_current_location_allowed',
      );
    } catch (e) {
      // User denied geolocation permission
      if (e.code === 1) {
        trackGenericEvent(
          SEGMENT_EVENTS.GEOLOCATION_DISALLOWED,
          'get_current_location_disallowed',
        );
      }
    }
  };

  const seeAllEventsOnClick = () => {
    setPoint(
      locationName,
      { lat: latitude, lng: longitude, radius: 50 },
      { background: false },
    );
  };

  if (!loading && !(events && events.length)) {
    return null;
  }

  return (
    <div className={container}>
      <div className={header}>
        <h4 className={title}>{getTitle({ loading, sport, locationName })}</h4>
        {showNotCorrectLocationButton && (
          <div className={correctLocationContainer}>
            <span className={notCorrectSpan}>Not correct?</span>
            <span
              className={torchRedLink}
              onClick={getLocationOnClick}
              onKeyDown={getLocationOnClick}
            >
              <Icon icon={Icons.LOCATION_ARROW.RED} />
              Get current location
            </span>
          </div>
        )}
        <span
          onClick={seeAllEventsOnClick}
          onKeyDown={seeAllEventsOnClick}
          className={classnames(torchRedLink, showDesktop)}
        >
          See all
        </span>
      </div>

      {!loading && events ? (
        <>
          <TileSet>
            {events.map((event, index) => (
              <EventTile
                onClick={(params) =>
                  trackButtonClick('event', {
                    eventIndex: index,
                    locationAccuracy,
                    locationName,
                    ...params,
                  })
                }
                event={event}
                key={event.id}
              />
            ))}
          </TileSet>

          <span
            onClick={seeAllEventsOnClick}
            onKeyDown={seeAllEventsOnClick}
            className={classnames(torchRedLink, showTablet)}
          >
            {getSeeAllTitle({ sport, locationName })}
          </span>
        </>
      ) : (
        <LoadingTileSet />
      )}
    </div>
  );
};

const NearbyEventsWithTrackingAndLocation = withTracking('nearby_events')(
  withLocationInfo(NearbyEvents),
);

const NearbyEventsWithGraphQL = ({ searchParams, ...props }) => {
  const {
    [ContextName.LOCATION.LATITUDE]: latitude,
    [ContextName.LOCATION.LONGITUDE]: longitude,
    [ContextName.LOCATION.NAME]: locationName,
    [ContextName.LOCATION.STATUS]: locationStatus,
  } = useContext(AppContext);

  if (locationStatus === StatusEnum.LOADING) {
    return <NearbyEventsWithTrackingAndLocation loading {...props} />;
  }

  if (!latitude || !longitude) {
    return null;
  }

  return (
    <Query
      query={SearchEventsV2}
      variables={{
        searchParametersInput: getSearchParams({
          ...searchParams,
          ...getEmptyViewpointState(),
          locationSearchRadius: 50,
          locationLat: latitude,
          locationLong: longitude,
        }),
      }}
    >
      {(gqlData) => (
        <NearbyEventsWithTrackingAndLocation
          {...gqlData}
          {...props}
          locationName={locationName}
        />
      )}
    </Query>
  );
};

export default NearbyEventsWithGraphQL;
